import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, errorMessage } from '../../lib/api.js';
import {
  useCategories, useWidget, useInvalidate, useMutation, useQuery, type Widget,
} from '../../lib/queries.js';
import {
  Badge, Button, Card, ColorInput, ConfirmDialog, CopyButton, EmptyState, ErrorBox, Input,
  Loading, Modal, PageHeader, Select, Tabs, Textarea, Toggle, STATUS_TONE, useToast, cx, Stat,
  IconRefresh, IconExternal,
} from '../../components/ui/index.js';
import { STATUS_LABEL, num, pct, relative, date } from '../../lib/format.js';
import { hasRole, useRole } from '../../lib/auth.js';

interface ConfigField {
  key: string; label: string; type: 'boolean' | 'number' | 'text' | 'select' | 'color' | 'textarea';
  group: 'content' | 'display' | 'theme' | 'advanced';
  min?: number; max?: number; step?: number;
  options?: { value: string | number; label: string }[];
  help?: string;
}

const GROUP_LABEL: Record<ConfigField['group'], string> = {
  content: 'เนื้อหาและข้อความ',
  display: 'การแสดงผล',
  theme: 'สีและรูปทรง',
  advanced: 'ขั้นสูง',
};

/** อ่าน/เขียนค่าใน config ที่อาจเป็น key แบบ "theme.primary" */
function readPath(obj: Record<string, any>, path: string): any {
  return path.split('.').reduce<any>((acc, k) => (acc == null ? acc : acc[k]), obj);
}
function writePath(obj: Record<string, any>, path: string, value: unknown): Record<string, any> {
  const keys = path.split('.');
  const next = { ...obj };
  let cursor: any = next;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i]!;
    cursor[k] = { ...(cursor[k] ?? {}) };
    cursor = cursor[k];
  }
  cursor[keys[keys.length - 1]!] = value;
  return next;
}

function ConfigControl({
  field, value, onChange, disabled,
}: { field: ConfigField; value: any; onChange: (v: any) => void; disabled?: boolean }) {
  switch (field.type) {
    case 'boolean':
      return <Toggle label={field.label} hint={field.help} checked={Boolean(value)}
                     onChange={onChange} disabled={disabled} />;
    case 'number':
      return <Input label={field.label} type="number" min={field.min} max={field.max} step={field.step}
                    hint={field.help} disabled={disabled} value={value ?? ''}
                    onChange={(e) => onChange(Number(e.target.value))} />;
    case 'select':
      return (
        <Select label={field.label} hint={field.help} disabled={disabled} value={String(value ?? '')}
                onChange={(e) => onChange(e.target.value)}>
          {field.options?.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
        </Select>
      );
    case 'color':
      return <ColorInput label={field.label} hint={field.help} value={String(value ?? '#000000')} onChange={onChange} />;
    case 'textarea':
      return <Textarea label={field.label} hint={field.help} rows={5} disabled={disabled}
                       value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}
                       className="font-mono text-xs" />;
    default:
      return <Input label={field.label} hint={field.help} disabled={disabled} value={String(value ?? '')}
                    onChange={(e) => onChange(e.target.value)} />;
  }
}

// ============================== Builder tab ==============================

function BuilderTab({ widget }: { widget: Widget }) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const role = useRole();
  const canEdit = hasRole(role, 'ADMIN');
  const categories = useCategories(widget.blogId);

  const schema = useQuery({
    queryKey: ['widget-schema', widget.id, widget.templateCode],
    queryFn: () => api.get<{ data: { fields: ConfigField[]; defaults: Record<string, any> } }>(
      `/v1/widgets/${widget.id}/config-schema`).then((r) => r.data),
  });

  const [config, setConfig] = useState<Record<string, any>>(widget.config);
  const [filters, setFilters] = useState({
    categoryIds: widget.filterCategoryIds,
    featuredOnly: widget.filterFeaturedOnly,
    sortBy: widget.sortBy,
  });
  const [previewKey, setPreviewKey] = useState(0);
  const [group, setGroup] = useState<ConfigField['group']>('content');

  // iframe แนบ Authorization header ไม่ได้ จึงขอ URL สาธารณะของหน้า preview มาใช้แทน
  const previewUrl = useQuery({
    queryKey: ['widget-preview-url', widget.id],
    queryFn: () => api.get<{ data: { url: string } }>(`/v1/widgets/${widget.id}/preview-url`).then((r) => r.data.url),
    staleTime: Infinity,
  });

  // config จาก server เปลี่ยน (เช่นหลังบันทึก) → sync กลับเข้าฟอร์ม
  useEffect(() => { setConfig(widget.config); }, [widget.config]);

  const dirty =
    JSON.stringify(config) !== JSON.stringify(widget.config) ||
    JSON.stringify(filters.categoryIds) !== JSON.stringify(widget.filterCategoryIds) ||
    filters.featuredOnly !== widget.filterFeaturedOnly ||
    filters.sortBy !== widget.sortBy;

  const save = useMutation({
    mutationFn: () => api.patch(`/v1/widgets/${widget.id}`, {
      config,
      filterCategoryIds: filters.categoryIds,
      filterFeaturedOnly: filters.featuredOnly,
      sortBy: filters.sortBy,
    }),
    onSuccess: () => {
      invalidate(['widget', widget.id], ['widgets']);
      toast('บันทึกแล้ว — เว็บที่ฝัง widget อยู่จะเห็นการเปลี่ยนแปลงภายใน 5 นาที');
      setPreviewKey((k) => k + 1);
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const fields = schema.data?.fields ?? [];
  const groups = ['content', 'display', 'theme', 'advanced'] as const;
  const visible = fields.filter((f) => f.group === group);

  return (
    <div className="grid gap-6 lg:grid-cols-[400px_minmax(0,1fr)]">
      <div className="space-y-4">
        <Card className="overflow-hidden">
          <Tabs
            active={group}
            onChange={(g) => setGroup(g as ConfigField['group'])}
            tabs={groups.map((g) => ({ id: g, label: GROUP_LABEL[g] }))}
          />
          <div className="max-h-[560px] space-y-4 overflow-y-auto p-4">
            {schema.isLoading ? <Loading label="" /> : visible.map((f) => (
              <ConfigControl
                key={f.key} field={f} disabled={!canEdit}
                value={readPath(config, f.key) ?? readPath(schema.data?.defaults ?? {}, f.key)}
                onChange={(v) => setConfig((c) => writePath(c, f.key, v))}
              />
            ))}
          </div>
        </Card>

        <Card className="space-y-4 p-4">
          <h3 className="text-sm font-semibold text-[var(--ink)]">กรองบทความที่จะแสดง</h3>
          <Select label="เรียงลำดับ" value={filters.sortBy} disabled={!canEdit}
                  onChange={(e) => setFilters((f) => ({ ...f, sortBy: e.target.value }))}>
            <option value="PINNED_FIRST">ปักหมุดก่อน แล้วเรียงตามวันที่ล่าสุด</option>
            <option value="PUBLISHED_DESC">ใหม่สุดก่อน</option>
            <option value="PUBLISHED_ASC">เก่าสุดก่อน</option>
            <option value="POPULAR">ยอดอ่านมากสุดก่อน</option>
          </Select>

          <div>
            <span className="label">เฉพาะหมวดหมู่</span>
            {categories.data?.length ? (
              <div className="flex flex-wrap gap-1.5">
                {categories.data.map((c) => {
                  const on = filters.categoryIds.includes(c.id);
                  return (
                    <button
                      key={c.id} type="button" disabled={!canEdit}
                      onClick={() => setFilters((f) => ({
                        ...f,
                        categoryIds: on ? f.categoryIds.filter((x) => x !== c.id) : [...f.categoryIds, c.id],
                      }))}
                      className={cx(
                        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                        on ? 'border-[var(--brand)] bg-[var(--brand)] text-white'
                           : 'border-[var(--line)] text-[var(--ink-2)] hover:border-[var(--ink-3)]',
                      )}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="hint">บล็อกนี้ยังไม่มีหมวดหมู่</p>
            )}
            <p className="hint">ไม่เลือกเลย = แสดงบทความจากทุกหมวดหมู่</p>
          </div>

          <Toggle label="เฉพาะบทความแนะนำ" checked={filters.featuredOnly} disabled={!canEdit}
                  onChange={(v) => setFilters((f) => ({ ...f, featuredOnly: v }))}
                  hint="ดึงเฉพาะบทความที่ติดธง 'บทความแนะนำ' ไว้" />
        </Card>

        {/* เว้นที่ท้ายคอลัมน์ให้ปุ่ม sticky ไม่ทับการ์ดใบสุดท้าย */}
        {canEdit && (
          <div className="sticky bottom-4 flex gap-2 rounded-xl bg-[color-mix(in_srgb,var(--bg)_92%,transparent)] p-2 backdrop-blur">
            <Button className="flex-1" loading={save.isPending} disabled={!dirty} onClick={() => save.mutate()}>
              {dirty ? 'บันทึกการเปลี่ยนแปลง' : 'บันทึกแล้ว'}
            </Button>
            {dirty && (
              <Button variant="ghost" onClick={() => {
                setConfig(widget.config);
                setFilters({
                  categoryIds: widget.filterCategoryIds,
                  featuredOnly: widget.filterFeaturedOnly,
                  sortBy: widget.sortBy,
                });
              }}>ย้อนกลับ</Button>
            )}
          </div>
        )}
        <div className="h-2" />
      </div>

      {/* ข้อ 9 — preview ใช้ widget.js ตัวจริงใน iframe เห็นตรงกับของจริง 100% */}
      <Card className="overflow-hidden lg:sticky lg:top-20 lg:self-start">
        <div className="flex items-center justify-between gap-3 border-b border-[var(--line)] px-4 py-2.5">
          <span className="text-sm font-medium text-[var(--ink-2)]">ตัวอย่างจริง</span>
          <Button size="sm" variant="ghost" icon={<IconRefresh size={14} />} onClick={() => setPreviewKey((k) => k + 1)}>รีเฟรช</Button>
        </div>
        {previewUrl.data ? (
          <iframe
            key={`${previewKey}-${previewUrl.data}`}
            src={previewUrl.data}
            title="ตัวอย่าง widget"
            className="h-[600px] w-full border-0 bg-[var(--sunken)]"
            sandbox="allow-scripts allow-same-origin allow-popups"
          />
        ) : (
          <div className="h-[600px]"><Loading label="" /></div>
        )}
        {dirty && (
          <div className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
            ตัวอย่างยังแสดงค่าที่บันทึกไว้ล่าสุด — กด &ldquo;บันทึกการเปลี่ยนแปลง&rdquo; เพื่อดูผลของค่าใหม่
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================== Embed tab ==============================

interface EmbedData {
  publicKey: string;
  snippets: { id: string; name: string; description: string; code: string }[];
  guides: { platform: string; steps: string[] }[];
  checklist: string[];
}

function EmbedTab({ widget }: { widget: Widget }) {
  const toast = useToast();
  const invalidate = useInvalidate();
  const role = useRole();
  const [regenerating, setRegenerating] = useState(false);

  const embed = useQuery({
    queryKey: ['embed', widget.id],
    queryFn: () => api.get<{ data: EmbedData }>(`/v1/widgets/${widget.id}/embed-code`).then((r) => r.data),
  });

  const regenerate = useMutation({
    mutationFn: () => api.post<{ warning: string }>(`/v1/widgets/${widget.id}/regenerate-key`),
    onSuccess: (r) => {
      invalidate(['widget', widget.id], ['widgets'], ['embed', widget.id]);
      toast(r.warning, 'info');
      setRegenerating(false);
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  if (embed.isLoading) return <Loading />;
  if (!embed.data) return <ErrorBox error={embed.error} />;

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-4">
        {embed.data.checklist.map((note, i) => (
          <div key={i} className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {note}
          </div>
        ))}

        {embed.data.snippets.map((s, i) => (
          <Card key={s.id} className="overflow-hidden">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[var(--line)] px-4 py-3">
              <div className="min-w-0">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--ink)]">
                  {s.name}
                  {i === 0 && <Badge tone="green">แนะนำ</Badge>}
                </h3>
                <p className="mt-0.5 text-xs text-[var(--ink-3)]">{s.description}</p>
              </div>
              <CopyButton text={s.code} variant={i === 0 ? 'primary' : 'secondary'} label="คัดลอกโค้ด" />
            </div>
            <pre className="overflow-x-auto bg-[var(--ink)] px-4 py-3 text-xs leading-relaxed text-[var(--surface)]">
              <code>{s.code}</code>
            </pre>
          </Card>
        ))}
      </div>

      <div className="space-y-4">
        <Card className="p-4">
          <h3 className="mb-2 text-sm font-semibold text-[var(--ink)]">คีย์สาธารณะ</h3>
          <p className="break-all rounded-lg bg-[var(--sunken)] px-3 py-2 font-mono text-xs">
            {embed.data.publicKey}
          </p>
          <p className="hint">คีย์นี้อยู่ในโค้ดฝั่งหน้าเว็บ ใครก็เห็นได้ — ความปลอดภัยมาจากรายการโดเมนที่อนุญาต ไม่ใช่ความลับของคีย์</p>
          {hasRole(role, 'ADMIN') && (
            <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => setRegenerating(true)}>
              สร้างคีย์ใหม่
            </Button>
          )}
        </Card>

        <Card className="p-4">
          <h3 className="mb-3 text-sm font-semibold text-[var(--ink)]">คู่มือติดตั้ง</h3>
          <div className="space-y-3">
            {embed.data.guides.map((g) => (
              <details key={g.platform} className="rounded-lg border border-[var(--line)]">
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-[var(--ink-2)]">
                  {g.platform}
                </summary>
                <ol className="list-decimal space-y-1 px-3 pb-3 pl-8 text-xs text-[var(--ink-2)]">
                  {g.steps.map((step, i) => <li key={i}>{step}</li>)}
                </ol>
              </details>
            ))}
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={regenerating} onClose={() => setRegenerating(false)}
        onConfirm={() => regenerate.mutate()} loading={regenerate.isPending} danger
        confirmLabel="สร้างคีย์ใหม่" title="สร้างคีย์สาธารณะใหม่?"
        message="โค้ดที่ฝังอยู่บนเว็บตอนนี้จะหยุดทำงานทันที คุณต้องนำโค้ดชุดใหม่ไปวางแทนทุกหน้าที่ฝัง widget นี้ — ทำเมื่อสงสัยว่าคีย์รั่วเท่านั้น"
      />
    </div>
  );
}

// ============================== Domains tab ==============================

function DomainsTab({ widget }: { widget: Widget }) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const role = useRole();
  const canEdit = hasRole(role, 'ADMIN');
  const [domain, setDomain] = useState('');

  const domains = useQuery({
    queryKey: ['widget-domains', widget.id],
    queryFn: () => api.get<{ data: { id: string; domain: string; createdAt: string }[] }>(
      `/v1/widgets/${widget.id}/domains`).then((r) => r.data),
  });

  const add = useMutation({
    mutationFn: () => api.post(`/v1/widgets/${widget.id}/domains`, { domain: domain.trim() }),
    onSuccess: () => {
      invalidate(['widget-domains', widget.id], ['widget', widget.id]);
      toast('เพิ่มโดเมนแล้ว'); setDomain('');
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/v1/widgets/${widget.id}/domains/${id}`),
    onSuccess: () => { invalidate(['widget-domains', widget.id], ['widget', widget.id]); toast('ลบโดเมนแล้ว'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const toggleLock = useMutation({
    mutationFn: (locked: boolean) => api.patch(`/v1/widgets/${widget.id}`, { isDomainLocked: locked }),
    onSuccess: () => { invalidate(['widget', widget.id]); toast('บันทึกแล้ว'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  return (
    <div className="max-w-2xl space-y-5">
      <Card className="p-4">
        <Toggle
          label="จำกัดให้แสดงเฉพาะโดเมนที่อนุญาต" checked={widget.isDomainLocked} disabled={!canEdit}
          onChange={(v) => toggleLock.mutate(v)}
          hint="แนะนำให้เปิดไว้เสมอ — ถ้าปิด ใครก็ตามที่เห็นคีย์ในโค้ดหน้าเว็บของคุณจะเอาไปใช้บนเว็บตัวเองได้ ซึ่งกินโควตาของคุณ"
        />
      </Card>

      {widget.isDomainLocked && (
        <>
          {canEdit && (
            <Card className="p-4">
              <form className="flex flex-wrap items-end gap-2"
                    onSubmit={(e) => { e.preventDefault(); if (domain.trim()) add.mutate(); }}>
                <div className="min-w-[220px] flex-1">
                  <Input label="เพิ่มโดเมน" placeholder="www.company.co.th" value={domain}
                         onChange={(e) => setDomain(e.target.value)}
                         hint="ใส่ *.company.co.th เพื่ออนุญาตทุกโดเมนย่อย" />
                </div>
                <Button type="submit" loading={add.isPending} disabled={!domain.trim()}>เพิ่ม</Button>
              </form>
            </Card>
          )}

          {domains.isLoading ? <Loading label="" /> : !domains.data?.length ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm">
              <p className="font-medium text-[var(--bad)]">ยังไม่มีโดเมนที่อนุญาต</p>
              <p className="mt-1 text-[var(--bad)]">
                ตอนนี้ widget จะไม่แสดงบนเว็บใดเลย — เพิ่มโดเมนของเว็บไซต์คุณด้านบนก่อน
              </p>
            </div>
          ) : (
            <Card className="divide-y divide-[var(--line-soft)]">
              {domains.data.map((d) => (
                <div key={d.id} className="flex items-center gap-3 px-4 py-3">
                  <span className="min-w-0 flex-1 truncate font-mono text-sm text-[var(--ink)]">{d.domain}</span>
                  <span className="shrink-0 text-xs text-[var(--ink-3)]">เพิ่มเมื่อ {date(d.createdAt)}</span>
                  {canEdit && (
                    <Button size="sm" variant="ghost" onClick={() => remove.mutate(d.id)}>ลบ</Button>
                  )}
                </div>
              ))}
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ============================== Stats tab ==============================

interface WidgetStats {
  widget: { id: string; name: string; templateCode: string };
  range: { from: string; to: string };
  totals: { impressions: number; clicks: number; ctr: number };
  series: { date: string; impressions: number; clicks: number }[];
  topPosts: { id: string; title: string; clicks: number; url: string | null }[];
  topReferrers: { domain: string | null; count: number }[];
  byDevice: { device: string; count: number }[];
}

const DEVICE_LABEL: Record<string, string> = {
  DESKTOP: 'คอมพิวเตอร์', MOBILE: 'มือถือ', TABLET: 'แท็บเล็ต', BOT: 'บอท', UNKNOWN: 'ไม่ทราบ',
};

function StatsTab({ widget }: { widget: Widget }) {
  const stats = useQuery({
    queryKey: ['widget-stats', widget.id],
    queryFn: () => api.get<{ data: WidgetStats }>(`/v1/widgets/${widget.id}/stats`).then((r) => r.data),
  });

  if (stats.isLoading) return <Loading />;
  if (!stats.data) return <ErrorBox error={stats.error} />;
  const s = stats.data;
  const max = Math.max(1, ...s.series.map((d) => d.impressions));

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Impression" value={num(s.totals.impressions)} sub="จำนวนครั้งที่ widget ถูกเห็น (30 วัน)" />
        <Stat label="คลิก" value={num(s.totals.clicks)} sub="กดเข้าอ่านบทความ" />
        <Stat label="CTR" value={pct(s.totals.ctr)} sub="คลิก ÷ impression" />
      </div>

      <Card className="p-5">
        <h3 className="mb-4 text-sm font-semibold text-[var(--ink)]">แนวโน้มรายวัน</h3>
        {s.totals.impressions === 0 ? (
          <p className="py-8 text-center text-sm text-[var(--ink-3)]">
            ยังไม่มีข้อมูล — ตัวเลขจะเริ่มขึ้นเมื่อมีคนเข้าชมหน้าเว็บที่ฝัง widget นี้
          </p>
        ) : (
          <svg viewBox="0 0 100 34" className="h-32 w-full" preserveAspectRatio="none" role="img"
               aria-label="แนวโน้ม impression และคลิกรายวัน">
            {s.series.map((d, i) => {
              const bw = 100 / s.series.length;
              return (
                <g key={d.date}>
                  <rect x={i * bw + 0.3} y={34 - (d.impressions / max) * 34}
                        width={bw - 0.6} height={(d.impressions / max) * 34}
                        className="fill-brand-500/25" rx="0.4" />
                  <rect x={i * bw + 0.3} y={34 - (d.clicks / max) * 34}
                        width={bw - 0.6} height={(d.clicks / max) * 34}
                        className="fill-brand-500" rx="0.4" />
                  <title>{`${d.date} — impression ${d.impressions} · คลิก ${d.clicks}`}</title>
                </g>
              );
            })}
          </svg>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5">
          <h3 className="mb-3 text-sm font-semibold text-[var(--ink)]">บทความที่ถูกคลิกมากสุด</h3>
          {s.topPosts.length ? (
            <ul className="space-y-2">
              {s.topPosts.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 text-sm">
                  {p.url ? (
                    <a href={p.url} target="_blank" rel="noopener" className="min-w-0 truncate hover:text-[var(--brand)]">{p.title}</a>
                  ) : <span className="min-w-0 truncate text-[var(--ink-3)]">{p.title}</span>}
                  <span className="shrink-0 tabular-nums text-[var(--ink-3)]">{num(p.clicks)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-[var(--ink-3)]">ยังไม่มีข้อมูล</p>}
        </Card>

        <Card className="p-5">
          <h3 className="mb-3 text-sm font-semibold text-[var(--ink)]">เว็บที่ฝัง widget</h3>
          {s.topReferrers.length ? (
            <ul className="space-y-2">
              {s.topReferrers.map((r) => (
                <li key={r.domain ?? 'unknown'} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate">{r.domain ?? '(ไม่ทราบ)'}</span>
                  <span className="shrink-0 tabular-nums text-[var(--ink-3)]">{num(r.count)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-[var(--ink-3)]">ยังไม่มีข้อมูล</p>}
        </Card>

        <Card className="p-5">
          <h3 className="mb-3 text-sm font-semibold text-[var(--ink)]">อุปกรณ์ของผู้ชม</h3>
          {s.byDevice.length ? (
            <ul className="space-y-2">
              {s.byDevice.map((d) => (
                <li key={d.device} className="flex items-center justify-between gap-3 text-sm">
                  <span>{DEVICE_LABEL[d.device] ?? d.device}</span>
                  <span className="tabular-nums text-[var(--ink-3)]">{num(d.count)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-[var(--ink-3)]">ยังไม่มีข้อมูล</p>}
        </Card>
      </div>
    </div>
  );
}

// ============================== Page ==============================

export function WidgetBuilderPage() {
  const { id } = useParams();
  const widget = useWidget(id);
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'builder';
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const toast = useToast();
  const role = useRole();
  const [deleting, setDeleting] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.del(`/v1/widgets/${id}`),
    onSuccess: () => { invalidate(['widgets'], ['quota']); toast('ลบ widget แล้ว'); navigate('/widgets'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const pause = useMutation({
    mutationFn: (status: 'ACTIVE' | 'PAUSED') => api.patch(`/v1/widgets/${id}`, { status }),
    onSuccess: () => { invalidate(['widget', id], ['widgets']); toast('เปลี่ยนสถานะแล้ว'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  if (widget.isLoading) return <Loading />;
  if (widget.error) return <ErrorBox error={widget.error} onRetry={() => void widget.refetch()} />;
  if (!widget.data) return null;
  const w = widget.data;

  return (
    <>
      <PageHeader
        breadcrumb={
          <nav className="mb-2 text-sm text-[var(--ink-3)]">
            <Link to="/widgets" className="hover:text-[var(--brand)]">Widget</Link>
            <span className="mx-1.5 text-[var(--ink-3)]">/</span>
            <span>{w.name}</span>
          </nav>
        }
        title={w.name}
        description={<span className="font-mono text-xs">{w.publicKey}</span>}
        actions={
          <>
            <Badge tone={STATUS_TONE[w.status] ?? 'gray'}>{STATUS_LABEL[w.status] ?? w.status}</Badge>
            {hasRole(role, 'ADMIN') && (
              <>
                <Button size="sm" variant="outline" loading={pause.isPending}
                        onClick={() => pause.mutate(w.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE')}>
                  {w.status === 'ACTIVE' ? 'หยุดชั่วคราว' : 'เปิดใช้งาน'}
                </Button>
                <Button size="sm" variant="ghost" className="text-[var(--bad)]" onClick={() => setDeleting(true)}>ลบ</Button>
              </>
            )}
          </>
        }
      />

      <Tabs
        active={tab} onChange={(t) => setParams({ tab: t }, { replace: true })}
        tabs={[
          { id: 'builder', label: 'ออกแบบ' },
          { id: 'embed', label: 'โค้ดติดตั้ง' },
          { id: 'domains', label: 'โดเมนที่อนุญาต', badge: w.domains.length },
          { id: 'stats', label: 'สถิติ' },
        ]}
      />

      <div className="mt-6">
        {tab === 'builder' && <BuilderTab widget={w} />}
        {tab === 'embed' && <EmbedTab widget={w} />}
        {tab === 'domains' && <DomainsTab widget={w} />}
        {tab === 'stats' && <StatsTab widget={w} />}
      </div>

      <ConfirmDialog
        open={deleting} onClose={() => setDeleting(false)} onConfirm={() => remove.mutate()}
        loading={remove.isPending} danger confirmLabel="ลบ widget" title="ลบ widget นี้?"
        message="โค้ดที่ฝังอยู่บนเว็บจะหยุดแสดงผลทันที (กล่องจะว่างเปล่า ไม่ขึ้น error ให้ผู้ชมเห็น)"
      />
    </>
  );
}
